import { ChangeEvent, FC } from "react";
import { CheckIcon, XCircleIcon } from "@heroicons/react/20/solid";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
} from "@headlessui/react";
import { cn } from "helpers/classNames";

export interface ComboBoxItem {
  id: string;
  name: string;
  description?: string;
}

interface Props {
  label?: string;
  items?: ComboBoxItem[];
  setQuery: (value: string) => void;
  selectedItem?: ComboBoxItem;
  setSelectedItem: (value: ComboBoxItem) => void;
  errorMessage?: string;
  className?: string;
}

export const ComboBox: FC<Props> = ({
  label,
  setQuery,
  items = [],
  selectedItem,
  setSelectedItem,
  errorMessage,
  className,
}) => {
  return (
    <Combobox
      as="div"
      value={selectedItem}
      onChange={setSelectedItem}
      className={className}
    >
      {!!label && (
        <Label className="flex text-sm font-semibold leading-6 text-gray-800">
          {label}
        </Label>
      )}

      <div className="relative mt-1">
        <ComboboxInput
          autoComplete="off"
          className="w-full rounded-md border-0 bg-white py-2 pl-3 pr-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm sm:leading-6"
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setQuery(event.target.value)
          }
          // displayValue={(item: ComboBoxItem) => item.name}
          displayValue={(item?: ComboBoxItem) =>
            !item || !item.id ? "" : `${item.name} - ${item.description}`
          }
          // placeholder="Sök avfallskod eller beskrivning"
        />
        {!!selectedItem && !!selectedItem.id && (
          <button
            type="button"
            onClick={() => {
              setSelectedItem({ id: "", name: "", description: "" });
              setQuery("");
            }}
            className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none"
          >
            <XCircleIcon
              className="h-5 w-5 text-gray-400 hover:text-gray-300"
              aria-hidden="true"
            />
          </button>
        )}
        {/* <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </ComboboxButton> */}

        {items.length > 0 && (
          <ComboboxOptions className="ring-black absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-opacity-5 focus:outline-none sm:text-sm">
            {items.map((item) => {
              return (
                <ComboboxOption
                  key={item.id}
                  value={item}
                  className={({ focus }) =>
                    cn(
                      "now relative cursor-default select-none py-2 pl-3 pr-9",
                      focus ? "bg-cyan-600 text-white" : "text-gray-900",
                    )
                  }
                >
                  {({ focus }) => (
                    <>
                      <div className="flex">
                        <span
                          className={cn(
                            "shrink-0",
                            item.id === selectedItem?.id && "font-semibold",
                          )}
                        >
                          {item.name}
                        </span>
                        <span
                          className={cn(
                            "ml-3 text-gray-500",
                            focus ? "text-white" : "text-gray-600",
                          )}
                        >
                          {item.description}
                        </span>
                      </div>

                      {item.id === selectedItem?.id && (
                        <span
                          className={cn(
                            "absolute inset-y-0 right-0 flex items-center pr-4",
                            focus ? "text-white" : "text-cyan-600",
                          )}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </ComboboxOption>
              );
            })}
          </ComboboxOptions>
        )}
      </div>
      {!!errorMessage && (
        <p className="mt-1 text-sm text-red-600">{errorMessage}</p>
      )}
    </Combobox>
  );
};
